import { useTranslation } from 'react-i18next'
import { StringParam, useQueryParams } from 'use-query-params'
import { useEffect, useMemo, useState } from 'react'

import { ButtonText, Flex, Typography, SidePanel } from '@percent/lemonade'
import { useTasksAutosavePreference, TaskAutosaveModal, TaskAutosaveToggle } from '@percent/domain/grants'
import { CandidateTasksProps } from './CandidateTasks.types'
import { TasksList } from './tasksList/TasksList'
import { CandidateReview } from './candidateReview/CandidateReview'

export function CandidateTasks({
  tasks,
  isTaskViewOpen,
  onClose,
  organisationName,
  candidateData,
  refreshTasks,
  refreshFormSubmissions,
  refreshDocuments
}: Readonly<CandidateTasksProps>) {
  const [queryParams, setQueryParams] = useQueryParams({ formId: StringParam })
  const [selectedFormId, setSelectedFormId] = useState(queryParams.formId)

  const { shouldShowModal, isAutosaveEnabled, enableAutosave, disableAutosave } = useTasksAutosavePreference()
  const showAutoSaveToggle = !shouldShowModal

  const { t } = useTranslation()

  const resetFormId = () => setQueryParams({ formId: undefined })
  const selectTask = (formId: string) => setQueryParams({ formId })
  const handleAutoSaveToggle = (enabled: boolean) => (enabled ? enableAutosave() : disableAutosave())

  const closeAndClearFormId = () => {
    onClose()
    resetFormId()
  }

  const onTaskSubmitted = (hasDocuments: boolean) => {
    refreshTasks()
    refreshFormSubmissions()

    if (hasDocuments) {
      refreshDocuments()
    }
    resetFormId()
  }

  useEffect(() => {
    setSelectedFormId(queryParams.formId)
  }, [queryParams.formId])

  const taskId = useMemo(
    () => tasks.find(task => task.form?.formId === selectedFormId)?.id ?? '',
    [tasks, selectedFormId]
  )

  if (!isTaskViewOpen) {
    return null
  }

  return (
    <>
      <TaskAutosaveModal
        open={shouldShowModal}
        onClose={disableAutosave}
        onConfirm={enableAutosave}
        title={t('grants.tasks.autoSaveModal.title')}
        description={t('grants.tasks.autoSaveModal.description')}
        alertTitle={t('grants.tasks.autoSaveModal.importantTitle')}
        alertDescription={t('grants.tasks.autoSaveModal.importantDescription')}
        enableLabel={t('grants.tasks.autoSaveModal.turnOn')}
        dismissLabel={t('grants.tasks.autoSaveModal.notNow')}
      />
      <SidePanel
        title={t('grants.tasks.title')}
        onClose={closeAndClearFormId}
        testId="candidate-tasks"
        closeButtonTestId="close-tasks-view-button"
      >
        {selectedFormId && (
          <Flex direction="column" flex={1} gap={24} data-testid="form-submission-view">
            {tasks.length > 1 && (
              <ButtonText icon="arrow-left" onClick={resetFormId} data-testid="back-to-tasks-button">
                {t('grants.tasks.otherReviewTasks')}
              </ButtonText>
            )}
            {showAutoSaveToggle && tasks.length === 1 && (
              <TaskAutosaveToggle
                enabled={isAutosaveEnabled}
                onToggle={handleAutoSaveToggle}
                title={t('grants.tasks.autoSaveToggle.title')}
                description={t('grants.tasks.autoSaveToggle.description')}
              />
            )}
            <CandidateReview
              organisationName={organisationName}
              formId={selectedFormId}
              taskId={taskId}
              onTaskSubmitted={onTaskSubmitted}
              autosaveEnabled={isAutosaveEnabled}
              candidateData={candidateData}
            />
          </Flex>
        )}
        {!selectedFormId && (
          <>
            {showAutoSaveToggle && (
              <TaskAutosaveToggle
                enabled={isAutosaveEnabled}
                onToggle={handleAutoSaveToggle}
                title={t('grants.tasks.autoSaveToggle.title')}
                description={t('grants.tasks.autoSaveToggle.description')}
              />
            )}
            <Typography variant="default">
              {t('grants.tasks.description', { organisation: organisationName })}
            </Typography>
            <TasksList tasks={tasks} onSelectTask={selectTask} />
          </>
        )}
      </SidePanel>
    </>
  )
}
